import { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
  Typography,
} from '@material-ui/core';
import { useSnackbar } from 'notistack';

import api from '../../../../services/api';
import { ROUTES } from '../../../../constants';

// Imprimir etiqueta de produto (QR = TAG) — enfileira na API; quem
// imprime é o coletor 4LogX Operation via rede (ET2 · aprovado).

interface ITemplate {
  id: number;
  nome: string;
  padrao: boolean;
}

interface IPrinter {
  id: number;
  nome: string;
  host: string;
  padrao: boolean;
}

interface IProps {
  open: boolean;
  onClose: () => void;
  itemIds: number[];
  title: string;
}

export const PrintLabelDialog = ({ open, onClose, itemIds, title }: IProps) => {
  const [templates, setTemplates] = useState<ITemplate[]>([]);
  const [printers, setPrinters] = useState<IPrinter[]>([]);
  const [templateId, setTemplateId] = useState<number | ''>('');
  const [printerId, setPrinterId] = useState<number | ''>('');
  const [copias, setCopias] = useState(1);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const history = useHistory();

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    Promise.all([api.get('/labels/modelos'), api.get('/labels/impressoras')])
      .then(([modelos, impressoras]) => {
        const ts: ITemplate[] = modelos.data?.modelos || [];
        const ps: IPrinter[] = impressoras.data?.impressoras || [];
        setTemplates(ts);
        setPrinters(ps);
        setTemplateId(ts.find((t) => t.padrao)?.id ?? ts[0]?.id ?? '');
        setPrinterId(ps.find((p) => p.padrao)?.id ?? ps[0]?.id ?? '');
      })
      .catch(() => {
        setTemplates([]);
        setPrinters([]);
      })
      .finally(() => setLoading(false));
  }, [open]);

  const print = async () => {
    setSending(true);
    try {
      const response = await api.post('/labels/fila', {
        itemIds,
        templateId: templateId || undefined,
        printerId: printerId || undefined,
        copias,
      });
      if (response.data?.error) {
        enqueueSnackbar(`Não foi possível enfileirar: ${response.data.error}`, {
          variant: 'error',
        });
      } else {
        enqueueSnackbar(
          `${response.data?.enfileirados || itemIds.length} etiqueta(s) na fila — o coletor imprime em instantes.`,
          { variant: 'success' },
        );
        onClose();
      }
    } catch {
      enqueueSnackbar('Falha ao enfileirar a impressão.', { variant: 'error' });
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>🖨 Imprimir etiqueta</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="textSecondary" gutterBottom>
          {title}
        </Typography>
        {!loading && printers.length === 0 ? (
          <Typography variant="body2" style={{ marginTop: 8 }}>
            Nenhuma impressora cadastrada ainda.{' '}
            <Button
              size="small"
              color="primary"
              onClick={() => {
                onClose();
                history.push(ROUTES.labels);
              }}
            >
              Cadastrar em Configurações › Etiquetas
            </Button>
          </Typography>
        ) : (
          <>
            <TextField
              select
              fullWidth
              margin="dense"
              size="small"
              variant="outlined"
              label="Modelo"
              value={templateId}
              onChange={(e) => setTemplateId(Number(e.target.value))}
            >
              {templates.map((t) => (
                <MenuItem key={t.id} value={t.id}>
                  {t.nome}
                  {t.padrao ? ' ★' : ''}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              fullWidth
              margin="dense"
              size="small"
              variant="outlined"
              label="Impressora"
              value={printerId}
              onChange={(e) => setPrinterId(Number(e.target.value))}
            >
              {printers.map((p) => (
                <MenuItem key={p.id} value={p.id}>
                  {p.nome} ({p.host}){p.padrao ? ' ★' : ''}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              fullWidth
              margin="dense"
              size="small"
              variant="outlined"
              type="number"
              label="Cópias por etiqueta"
              value={copias}
              onChange={(e) =>
                setCopias(Math.min(Math.max(Number(e.target.value) || 1, 1), 99))
              }
            />
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button
          variant="contained"
          color="primary"
          onClick={print}
          disabled={sending || loading || printers.length === 0}
        >
          Imprimir
        </Button>
      </DialogActions>
    </Dialog>
  );
};
